import { Link } from "react-router-dom";
import {
  BookOpen,
  Sparkles,
  CreditCard,
  Wrench,
  ShieldCheck,
  Rocket,
  ListChecks,
  Target,
  BarChart3,
  Image as ImageIcon,
} from "lucide-react";

export type HelpSection = {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  intro: string;
  items: { q: string; a: React.ReactNode }[];
};

export const HELP_SECTIONS: HelpSection[] = [
  {
    id: "getting-started",
    label: "Getting started",
    icon: Rocket,
    intro: "What to do in the first hour so your first response is set up properly — not just fast.",
    items: [
      {
        q: "How do I start a new proposal?",
        a: (
          <>
            From the Proposals page click <strong>New Proposal</strong> and upload the buyer's tender document.
            Sorvaine reads the document, lifts every requirement, builds a draft outline against them, and
            pre-fills sections from your Knowledge Vault. You land on a working draft, not a blank canvas.
          </>
        ),
      },
      {
        q: "What can I upload as the tender document?",
        a: "PDF and DOCX. Scanned PDFs are OCR'd automatically, but the original digital file always gives cleaner requirement extraction — if the buyer published both, use the digital one.",
      },
      {
        q: "Do I need to set up the Knowledge Vault before my first proposal?",
        a: "Strongly recommended. You can technically start with an empty vault, but Sorvaine will return more placeholders and less evidence. Even 10–15 well-chosen entries (company overview, two or three case studies, your standard security/data protection responses, key accreditations) lift the first draft dramatically.",
      },
      {
        q: "Realistically, how long until I have a first draft?",
        a: "Setup takes a few minutes. A complete first-pass draft for a mid-size tender is typically same-day. The time you save isn't in writing — it's in not chasing SMEs for boilerplate you've already written before.",
      },
      {
        q: "Who should be in the platform from day one?",
        a: "At minimum: the bid lead and anyone who owns recurring content (security, HR, legal, finance). They don't need to write inside the tool, but their content needs to live in the Vault so it's available the next time you're asked for it.",
      },
    ],
  },
  {
    id: "knowledge-vault",
    label: "Knowledge Vault",
    icon: BookOpen,
    intro: "Your reusable answer library. The better this is, the less you write from scratch.",
    items: [
      {
        q: "What belongs in the Vault — and what doesn't?",
        a: (
          <>
            In: <strong>your</strong> answers, capability statements, case studies, methodologies, policies,
            accreditations, team bios, prior Q&amp;A. Out: the buyer's tender document — that gets uploaded
            when you create the <strong>Proposal</strong>, not the Vault. Keep buyer material and your own
            material strictly separate.
          </>
        ),
      },
      {
        q: "What's the highest-value content to add first?",
        a: "Three things, in order: (1) your last 2–3 winning proposals, (2) detailed case studies with named outcomes and metrics, (3) your standard security / data protection / health & safety / quality responses. That covers 70% of what gets asked in most tenders.",
      },
      {
        q: "What are the seven categories and why do they matter?",
        a: "Company Profile, Past Performance, Methodology, Team & People, Compliance & Policies, Pricing & Commercials, and Reference Materials. Sorvaine routes retrieval by category, so a methodology question doesn't pull a case study and vice versa. Mis-categorised content is the single most common reason answers feel off.",
      },
      {
        q: "How does Sorvaine actually pick which Vault entries to use?",
        a: "Every entry is converted into a vector embedding. When you generate a section, Sorvaine retrieves the most semantically relevant entries from the matching category, then writes the answer grounded only in those sources. Nothing comes from outside your Vault and the tender document.",
      },
      {
        q: "An entry shows 'Not searchable' — what does that mean?",
        a: (
          <>
            The entry is missing its embedding (usually a transient error during upload), so it won't be
            surfaced in retrieval. Click <strong>Re-index</strong> on the item — it rebuilds in a few
            seconds and the badge clears.
          </>
        ),
      },
      {
        q: "How granular should entries be?",
        a: "Smaller is better. Break long documents into single-topic entries (one case study per entry, one policy per entry, one capability per entry). Big mixed documents retrieve poorly because the embedding averages everything together.",
      },
      {
        q: "Can the whole team share one Vault?",
        a: "Yes — everyone in your organisation works against the same Vault. You can scope visibility further with Library teams in Settings if certain content (e.g. commercials) needs to stay restricted.",
      },
    ],
  },
  {
    id: "outline-requirements",
    label: "Outline",
    icon: ListChecks,
    intro: "How the response is structured, how to reshape it, and how to make sure nothing is missed.",
    items: [
      {
        q: "How is the outline built?",
        a: "Sorvaine extracts every requirement from the tender, then maps each one to a section. If the buyer prescribes a response structure, the outline mirrors it exactly so you don't get scored down on format. Otherwise it uses our default 13-section template.",
      },
      {
        q: "Can I restructure the outline?",
        a: "Yes — drag to reorder, click to rename, add or delete sections freely. Numbering and the table of contents in your Word export update automatically so you don't have to renumber by hand at submission.",
      },
      {
        q: "Why are some requirements mapped to more than one section?",
        a: "Because buyers often ask the same thing in two places (e.g. data security under 'IT' and again under 'Compliance'). Multi-mapping means both sections see the requirement, so you don't either miss it or contradict yourself across sections.",
      },
      {
        q: "How do I move a requirement to a different section?",
        a: (
          <>
            Open the <strong>Requirements</strong> tab, find the item, and use the section dropdown to remap
            it. The original section loses it instantly, the destination picks it up. Coverage recalculates
            on the next refresh.
          </>
        ),
      },
      {
        q: "What's the 'Unmapped' / fallback section?",
        a: "A safety net. If a requirement can't be mapped with high confidence, it lands here so it isn't quietly lost. Before submission, work through any items in this section and remap them — leaving content in 'Unmapped' is a common cause of evaluator deductions.",
      },
      {
        q: "Can I see only the mandatory requirements?",
        a: "Yes. The Requirements tab lets you filter by mandatory vs nice-to-have. Sorvaine treats mandatory items as gating — they must be addressed before a proposal can be marked ready for submission.",
      },
    ],
  },
  {
    id: "ai-behaviour",
    label: "AI behaviour",
    icon: Sparkles,
    intro: "How Sorvaine writes, what it refuses to do, and what every placeholder and banner actually means.",
    items: [
      {
        q: "Will Sorvaine ever make something up to fill a gap?",
        a: (
          <>
            No. If the answer isn't in the Vault or the tender, Sorvaine inserts a visible placeholder like{" "}
            <code className="px-1.5 py-0.5 rounded bg-muted text-xs">[INSERT: named client reference]</code>{" "}
            rather than inventing one. Placeholders block the section from being marked Complete, so nothing
            ships with fabricated detail.
          </>
        ),
      },
      {
        q: "What is the Factual Accuracy banner?",
        a: "An amber banner that flags specific claims (metrics, dates, named clients, accreditations) Sorvaine wrote but couldn't verify against a Vault source. You acknowledge each item individually — that creates an auditable record that a human approved the claim before submission.",
      },
      {
        q: "How are sources cited and tracked?",
        a: "Every generated paragraph is traceable to the Vault entries that fed it. Open the Sources panel on any section to see them. If you later edit or delete a source, the attributions are pruned automatically — so your audit trail never references content that no longer exists.",
      },
      {
        q: "Can I control tone and how bold the claims are?",
        a: "Yes, and they're set in different places. Tone of Voice is per-user in Settings (so a writer and a reviewer can each have their own). Risk Posture — how cautious or assertive Sorvaine is when making claims — is set at organisation level so the whole company speaks with one voice on commitments.",
      },
      {
        q: "How does 'Improve quality score' work?",
        a: "It re-runs your current draft through the model with the section's requirements and a critique-and-rewrite pass. Improvements stay strictly inside the section — Sorvaine won't introduce new commitments or facts that weren't there before.",
      },
      {
        q: "Why did Sorvaine refuse to answer one of my sections?",
        a: "Almost always because the Vault has no relevant content for that ask. Rather than write something generic that an evaluator would spot immediately, Sorvaine surfaces a placeholder so you can either add the source or write the section yourself.",
      },
    ],
  },
  {
    id: "buyer-intelligence",
    label: "Buyer intelligence",
    icon: Target,
    intro: "How Sorvaine helps you decide whether to bid and where the response is weak.",
    items: [
      {
        q: "What is the Go/No-Go verdict?",
        a: "A summary card combining mandatory-requirement coverage, your Vault's evidence depth for what the buyer is asking, and the tender's overall complexity. It's input to the decision, not the decision itself — commercial fit, incumbent risk, and pipeline value still sit with you.",
      },
      {
        q: "How is the coverage percentage calculated?",
        a: "Deterministically. It counts answered, scored requirements against total requirements. There's no AI judgement in the number, so it doesn't drift between page loads and you can defend it in a bid review.",
      },
      {
        q: "What does 'What Evaluators Look For' actually contain?",
        a: "Per-section guidance generated at outline creation that describes the criteria a typical evaluator would score against for this tender. Use it as a checklist when you review the draft — if your answer doesn't visibly address the listed criteria, you're leaving marks on the table.",
      },
      {
        q: "My sections are mostly green but coverage is low — why?",
        a: "Sections can be marked Complete while individual requirements inside them remain unanswered. The coverage metric tracks requirements, not section status, which is why it's the more honest number to report to your bid board.",
      },
      {
        q: "Can I use Buyer Intelligence before committing time to the bid?",
        a: "That's the intended use. Upload the tender, let the intelligence panel run, and you have a defensible Go/No-Go view inside an hour — instead of three days of SME meetings.",
      },
    ],
  },
  {
    id: "editing-exports",
    label: "Editing & exports",
    icon: ImageIcon,
    intro: "Inline images, the appendix, and how the final Word document is produced.",
    items: [
      {
        q: "How do I add an image inside a section?",
        a: "Use the image button in the section editor toolbar to upload a PNG or JPG. Images are stored in your organisation's media library and embedded directly into the Word export so they render reliably for the evaluator — no broken-link icons.",
      },
      {
        q: "When should I use the appendix instead?",
        a: "For anything large or supporting rather than core narrative — org charts, certifications, methodology diagrams, full CVs. Sorvaine references appendix items inline (e.g. 'see Appendix B') instead of inflating the main body, which keeps your scored sections tight.",
      },
      {
        q: "What does the Word export look like?",
        a: "Calibri 11pt body, brand accent in Sorvaine orange, sequentially numbered headings and table of contents based on your current outline order. The structure mirrors what an evaluator would expect to receive from a professional bid team.",
      },
      {
        q: "How do I keep a confidential client name out of the export?",
        a: "Enable NDA Protection at the proposal level. Any client names you've flagged as confidential are automatically redacted in the generated Word file — useful when you're submitting a case study under an NDA but still want to demonstrate the experience.",
      },
      {
        q: "Can I export before the proposal is finished?",
        a: "Yes — export any time. Placeholders and unverified-claim flags carry into the document so an internal reviewer can see exactly what still needs work. They are not included in the final submission version once resolved.",
      },
    ],
  },
  {
    id: "analytics",
    label: "Analytics & insights",
    icon: BarChart3,
    intro: "What feeds your dashboard, how win rate is calculated, and which Vault content is actually earning its place.",
    items: [
      {
        q: "What feeds the dashboard?",
        a: "Your proposals, their current stage (Draft, In Review, Submitted, Won, Lost), and the outcomes you record. Numbers are aggregated organisation-wide so the dashboard reflects the whole team, not just you.",
      },
      {
        q: "How is win rate calculated?",
        a: "Won ÷ (Won + Lost). Drafts and In-Review proposals are excluded so the figure only reflects decided outcomes — meaning the number you see is the one you can put in a board pack without an asterisk.",
      },
      {
        q: "What does 'Vault performance' actually measure?",
        a: "The number of distinct proposals each Vault entry has been cited in — not raw retrieval count. An entry cited across five winning proposals is doing real work. An entry with zero citations after several bids is a candidate to rewrite, recategorise, or retire.",
      },
      {
        q: "Why isn't a proposal I just submitted on the win-rate chart?",
        a: "Win-rate charts only include Won and Lost. Record the outcome from the proposal header once you hear back (or via the day-60 win/loss email) and it appears on the next refresh.",
      },
      {
        q: "How do I run a post-mortem on a lost bid?",
        a: "Open the proposal, mark it Lost, and add the reason. The dashboard groups loss reasons over time so you can see patterns (price, technical fit, incumbent advantage) rather than treating each loss as a one-off.",
      },
      {
        q: "Can I export the analytics?",
        a: "Not from the UI yet. If you need a one-off pull for a board pack or QBR, email support@sorvaine.com and we'll send it across.",
      },
    ],
  },
  {
    id: "billing",
    label: "Billing & plans",
    icon: CreditCard,
    intro: "Plans, invoices, refunds, and how to make changes.",
    items: [
      {
        q: "What's included on each plan?",
        a: (
          <>
            See the full comparison on the{" "}
            <Link to="/pricing" className="text-primary hover:underline">Pricing page</Link>. Plans differ on
            proposal volume, Vault size, seats, and access to features like NDA Protection. Enterprise adds
            SSO, audit logs, and a named success contact.
          </>
        ),
      },
      {
        q: "How do I change or cancel my plan?",
        a: (
          <>
            <Link to="/settings" className="text-primary hover:underline">Settings → Billing</Link> →{" "}
            <strong>Manage subscription</strong>. You can switch plans, update payment details, or cancel
            from there. Cancellations take effect at the end of the current billing period — no surprise
            cut-off mid-bid.
          </>
        ),
      },
      {
        q: "What happens if a payment fails?",
        a: "You get an email immediately, then again on day 3 and day 7 with a one-click recovery link. Your account stays fully active throughout. If payment isn't recovered after 7 days the subscription is cancelled automatically — your proposals and Vault are retained, not deleted.",
      },
      {
        q: "Can I get a refund?",
        a: "Yes — within 14 days of payment, no questions asked. Use Request refund in Settings → Billing or email support@sorvaine.com.",
      },
      {
        q: "Do you offer annual billing or custom pricing?",
        a: "Yes on Enterprise. Email support@sorvaine.com and we'll set it up — annual usually includes a discount and a single PO-friendly invoice.",
      },
      {
        q: "Can I get invoices made out to my company for procurement?",
        a: "Yes. Add your company name, billing address, and VAT/tax ID in Settings → Billing and every future invoice carries that detail. Past invoices can be reissued — email support@sorvaine.com.",
      },
    ],
  },
  {
    id: "troubleshooting",
    label: "Troubleshooting",
    icon: Wrench,
    intro: "The issues that come up most often and how to clear them quickly.",
    items: [
      {
        q: "My Word export looks misaligned",
        a: "Regenerate it — most layout issues come from inline images that hadn't finished processing on the first try. If it persists, open the section involved, re-save, and export again.",
      },
      {
        q: "A requirement seems to be missing from my outline",
        a: "Open the Requirements tab and check the fallback / 'Unmapped' section — anything Sorvaine couldn't confidently place lands there rather than being dropped. Remap it from the dropdown.",
      },
      {
        q: "AI generation is taking longer than usual",
        a: "Large tenders (50+ requirements) routinely take 60–90 seconds. If a single section is stuck for more than three minutes, refresh — your draft is autosaved continuously, so you won't lose work.",
      },
      {
        q: "I can't find a Vault entry I know exists",
        a: "Check the Embedding Health badge on the entry. If it shows 'Not searchable', click Re-index. If it's healthy, the entry may be filtered as off-topic for the current section — try rephrasing the section prompt or check the category is correct.",
      },
      {
        q: "A section was generated using the wrong case study",
        a: "Open the Sources panel for that section and remove the case study you don't want cited, then click Improve quality score. Sorvaine re-generates using only the remaining sources.",
      },
      {
        q: "How do I report a bug?",
        a: (
          <>
            Use <strong>Report a problem</strong> in your user menu (top-right). It auto-attaches your current
            page, browser, and proposal context, which means we can usually reproduce and fix without a back-
            and-forth.
          </>
        ),
      },
    ],
  },
  {
    id: "security",
    label: "Security & data",
    icon: ShieldCheck,
    intro: "Where your data lives, who can see it, and how to remove it.",
    items: [
      {
        q: "Where is my data stored?",
        a: "All proposal content, Vault entries, and uploaded documents are encrypted at rest in EU data centres. Access is scoped to your organisation — no other tenant can read your data, and our staff do not access tenant content except for support cases you raise.",
      },
      {
        q: "Does Sorvaine train AI models on my data?",
        a: "No. Your content is never used to train models. AI requests are processed by our providers under zero-retention agreements, so prompts and outputs are not stored on their side either.",
      },
      {
        q: "Who in my organisation can see what?",
        a: "By default, every member of your organisation sees the same proposals and Vault. If you need stricter scoping (commercials team only, regional teams), use Library teams in Settings to restrict visibility per category.",
      },
      {
        q: "How is client confidentiality protected in exports?",
        a: "Enable NDA Protection at the proposal level. Buyer names you've flagged confidential are redacted automatically in the generated Word file — so you can still cite the experience without breaching the NDA.",
      },
      {
        q: "How do I delete my data?",
        a: "Delete individual proposals or Vault entries from the UI any time. To delete the entire account and all associated data, email support@sorvaine.com — we process the request within 5 business days and confirm in writing.",
      },
      {
        q: "Where can I read your full security overview?",
        a: (
          <>
            On the <Link to="/security" className="text-primary hover:underline">Security page</Link>. It covers
            infrastructure, encryption, access controls, and the certifications we hold — useful if your
            procurement team needs to assess us as a vendor.
          </>
        ),
      },
    ],
  },
];
